// /draft — Limited deck builder. The user uploads photos of their
// physical draft pool (or pastes an Arena-format pool from MTGA) and
// the Oracle picks the best 40-card deck from the pool, applying
// Limited canon: 17 lands, BREAD composition, curve discipline,
// evasion count, splash math.
//
// Posts to /api/upload-deck with `mode: "limited"` and (optionally)
// `images`. The route's vision pre-pass transcribes attached photos
// into Arena format; if the user typed nothing, the transcription IS
// the pool. If they typed AND attached, both are concatenated.
//
// Built by mirroring two existing pages: UploadDeck (form shape,
// streaming render, save/redirect flow) and BuildDeck (file input,
// paste handler, thumbnail strip).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Paperclip, Square, Upload, X } from "lucide-react";
import {
  DeckUploadValidationError,
  getCardMetaBatch,
  streamUploadDeck,
  type Deck,
  type UploadDeckDoneEvent,
} from "../lib/api";
import type { AgentEvent } from "../lib/types";
import StreamingOutput from "../components/StreamingOutput";
import StreamingDeckResult from "../components/StreamingDeckResult";
import LiquidButton, { MANA_G } from "../components/ui/LiquidButton";
import MagicButton from "../components/ui/MagicButton";
import { useRefreshBalance } from "../lib/useBalance";
import { useMyUsername } from "../lib/useMyUsername";
import { useSubscription } from "../lib/useSubscription";
import { ACTION_COSTS } from "../lib/credits";
import { cn } from "../lib/cn";
import { parseArenaDeckText } from "../lib/arena-format";
import type { SectionKey } from "../lib/progressive-sections";
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGES,
  type AttachedImage,
  disposeAttachment,
  processFile,
} from "../lib/image-attachments";

type FormError = { theme?: string; pool?: string };

const SECTION_ORDER: SectionKey[] = [
  "winCondition",
  "keyInteractions",
  "manaBase",
  "sideboardGuide",
  "weaknesses",
];

// The pool placeholder shows what a typed pool looks like. Most users
// will arrive with photos and never see this — but for power users who
// already have an Arena export of their draft, the textarea is the
// faster path.
const POOL_PLACEHOLDER = `Deck
2 Selfless Glyphweaver
3 Banishing Light
1 Skyhunter Strike Force
2 Trumpeting Carnosaur
1 Heroic Reinforcements
1 Disenchant
2 Pacifism
1 Giant Growth
... (paste your entire pool — 45 cards from draft, ~90 from sealed)

Sideboard
1 Otherworldly Gaze
2 Verdant Embrace
... (anything you didn't fit above is fine here too)`;

export default function DraftBuilder() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState("");
  const [brief, setBrief] = useState("");
  const [pool, setPool] = useState("");
  const [running, setRunning] = useState(false);
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [lastText, setLastText] = useState("");
  const [submittedDeck, setSubmittedDeck] = useState<Deck | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormError>({});
  const abortRef = useRef<AbortController | null>(null);
  const themeInputRef = useRef<HTMLInputElement | null>(null);
  const poolInputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const refreshBalance = useRefreshBalance();
  const myUsername = useMyUsername();
  const { isSubscriber } = useSubscription();
  // Model preference for the agent loop. Defaults to Sonnet (parity
  // with /build) — active subscribers can flip to Opus per build.
  // Non-subscribers never see the toggle and the server silently
  // downgrades any "opus" request from a free user.
  const [modelPreference, setModelPreference] = useState<"sonnet" | "opus">("sonnet");

  // Image attachments (the primary input on this page). Mirror the
  // BuildDeck pattern: client-side downscale + JPEG re-encode keeps the
  // wire payload under the server's 2MB-per-image cap.
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);

  // Submit is allowed when EITHER images or pool text is present —
  // image-only and text-only flows are both first-class.
  const canSubmit = !running;

  const cardNames = useMemo(() => {
    if (!submittedDeck) return [] as string[];
    const names = new Set<string>();
    for (const entry of submittedDeck.mainboard) names.add(entry.name);
    for (const entry of submittedDeck.sideboard) names.add(entry.name);
    return [...names];
  }, [submittedDeck]);
  const streamMetaQuery = useQuery({
    queryKey: ["card-meta-batch", cardNames],
    queryFn: () => getCardMetaBatch(cardNames),
    enabled: cardNames.length > 0,
  });

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setImageError(null);
    let next: AttachedImage[] = [];
    setImages((prev) => {
      next = prev;
      return prev;
    });
    for (const file of list) {
      if (next.length >= MAX_IMAGES) {
        setImageError(`max ${MAX_IMAGES} images`);
        break;
      }
      try {
        const att = await processFile(file);
        next = [...next, att];
        setImages(next);
      } catch (e) {
        setImageError((e as Error).message);
      }
    }
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) disposeAttachment(target);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  // Catch image paste anywhere on the textarea. Image paste = Cmd-V of
  // a screenshot from clipboard; non-image paste falls through to the
  // textarea's default (so users can still paste an Arena pool).
  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        void addFiles(files);
      }
    },
    [addFiles],
  );

  // Whole-page drop target so users can drop a photo anywhere on the
  // form, not just onto the input. Filters non-image drops.
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer?.files ?? []).filter((f) =>
        f.type.startsWith("image/"),
      );
      if (files.length > 0) void addFiles(files);
    },
    [addFiles],
  );

  // Revoke object URLs on unmount so blobs aren't stranded.
  useEffect(() => {
    return () => {
      setImages((prev) => {
        prev.forEach(disposeAttachment);
        return [];
      });
    };
  }, []);

  const reset = (): void => {
    setEvents([]);
    setLastText("");
    setSubmittedDeck(null);
    setErrors([]);
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const next: FormError = {};
    if (theme.trim().length === 0) {
      next.theme = "Give your draft a label so it's easy to find later.";
    }
    if (pool.trim().length === 0 && images.length === 0) {
      next.pool = "Attach a photo of your pool, or paste it as text.";
    }
    if (next.theme || next.pool) {
      setFieldErrors(next);
      if (next.theme) themeInputRef.current?.focus();
      else if (next.pool) poolInputRef.current?.focus();
      return;
    }
    setFieldErrors({});

    // Optimistic preview from the typed pool only — image transcription
    // happens server-side and the parse comes back via SSE. If the user
    // submitted images-only, the StreamingDeckResult panel waits for
    // the agent's chosen deck to arrive in the streamed text instead.
    const previewDeck = pool.trim() ? parseArenaDeckText(pool.trim()) : null;
    reset();
    if (previewDeck) setSubmittedDeck(previewDeck);
    setRunning(true);
    const ctl = new AbortController();
    abortRef.current = ctl;
    try {
      await streamUploadDeck(
        {
          theme: theme.trim(),
          brief: brief.trim(),
          decklist: pool.trim(),
          mode: "limited",
          images: images.length > 0
            ? images.map((img) => ({
                media_type: img.media_type,
                data_b64: img.data_b64,
              }))
            : undefined,
          // Server validates subscription state and silently downgrades
          // non-subscribers, so the field is safe to send unconditionally
          // — but we only attach it when the user opted into Opus, to
          // keep the request body minimal in the common Sonnet path.
          ...(isSubscriber && modelPreference === "opus"
            ? { model_preference: "opus" as const }
            : {}),
        },
        (msg) => {
          if (msg.kind === "event") {
            setEvents((prev) => [...prev, msg.data]);
            if (msg.data.type === "assistant_text_delta") {
              const delta = msg.data.text;
              setLastText((prev) => prev + delta);
            } else if (msg.data.type === "turn_start") {
              setLastText("");
            } else if (msg.data.type === "assistant_text") {
              if (msg.data.text.length > 0) setLastText(msg.data.text);
            }
          } else if (msg.kind === "done") {
            refreshBalance();
            const done = msg.data as UploadDeckDoneEvent;
            // Surface the final picked deck once the run lands so the
            // result panel renders the agent's chosen 40, not the raw
            // pool the user uploaded.
            if (done.deck) setSubmittedDeck(done.deck);
            if (done.saved_id && myUsername) {
              navigate(`/decks/${myUsername}/${done.saved_id}`);
            }
          } else if (msg.kind === "error") {
            refreshBalance();
            setError(msg.data.error);
          } else if (msg.kind === "save_error") {
            setError(`(persist) ${msg.data.error}`);
          }
        },
        ctl.signal,
      );
    } catch (err) {
      if (err instanceof DeckUploadValidationError) {
        setSubmittedDeck(null);
        setErrors(err.errors);
      } else if ((err as Error).name !== "AbortError") {
        setSubmittedDeck(null);
        setError((err as Error).message);
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const onCancel = (): void => {
    abortRef.current?.abort();
  };

  return (
    <div
      className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-6"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <header>
        <div className="eyebrow">Draft</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          Upload your pool.
          <br />
          <span className="foil">Get the best 40-card deck.</span>
        </h1>
        <p className="text-sm text-fg-muted mt-5 max-w-xl leading-relaxed">
          Snap photos of your draft or sealed pool — or paste an Arena
          export — and the Oracle picks the best 40-card build. 17-land
          canon, BREAD composition, splash math, all applied automatically.
        </p>
      </header>

      {error && (
        <div className="surface p-4 border-danger/40 bg-danger/10 text-sm text-danger">
          {error}
        </div>
      )}

      {errors.length > 0 && (
        <div className="surface p-4 border-danger/40 bg-danger/10 space-y-2">
          <div className="text-sm font-medium text-danger">
            Pool didn't pass validation:
          </div>
          <ul className="text-sm text-danger list-disc list-inside space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <form onSubmit={onSubmit} className="surface p-5 space-y-4">
        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Draft label <span className="text-danger">*</span>
          </span>
          <input
            ref={themeInputRef}
            type="text"
            className="input w-full"
            placeholder="e.g. Strixhaven Sealed #2, OTJ Quick Draft"
            value={theme}
            onChange={(e) => {
              setTheme(e.target.value);
              if (fieldErrors.theme && e.target.value.trim().length > 0) {
                setFieldErrors((prev) => ({ ...prev, theme: undefined }));
              }
            }}
            disabled={running}
            maxLength={120}
            aria-invalid={!!fieldErrors.theme}
            aria-describedby={fieldErrors.theme ? "theme-error" : undefined}
          />
          {fieldErrors.theme && (
            <span id="theme-error" className="text-[11px] text-danger block">
              {fieldErrors.theme}
            </span>
          )}
        </label>

        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Notes (optional)
          </span>
          <textarea
            className="input w-full min-h-[64px] resize-y"
            placeholder="Anything the Oracle should know — pre-committed colors, a bomb you want to build around, sideboard plans you have in mind."
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            disabled={running}
          />
        </label>

        <div className="block space-y-1.5">
          <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Pool <span className="text-danger">*</span>
          </span>

          {(images.length > 0 || imageError) && (
            <div className="flex flex-wrap items-start gap-2 pb-1">
              {images.map((img) => (
                <div
                  key={img.id}
                  className="relative h-24 w-24 rounded-md overflow-hidden border border-border bg-bg-raised group"
                  title={`${img.filename} · ${(img.bytes / 1024).toFixed(0)} KB`}
                >
                  <img
                    src={img.preview_url}
                    alt={img.filename}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    disabled={running}
                    className="absolute top-1 right-1 rounded-full bg-black/70 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                    aria-label={`Remove ${img.filename}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {imageError && (
                <div className="text-xs text-danger self-center">
                  {imageError}
                </div>
              )}
            </div>
          )}

          <textarea
            ref={poolInputRef}
            className="input w-full min-h-[200px] font-mono text-[13px] leading-relaxed resize-y"
            placeholder={POOL_PLACEHOLDER}
            value={pool}
            onChange={(e) => {
              setPool(e.target.value);
              if (fieldErrors.pool && e.target.value.trim().length > 0) {
                setFieldErrors((prev) => ({ ...prev, pool: undefined }));
              }
            }}
            onPaste={onPaste}
            disabled={running}
            spellCheck={false}
            aria-invalid={!!fieldErrors.pool}
            aria-describedby={fieldErrors.pool ? "pool-error" : undefined}
          />
          {fieldErrors.pool && (
            <span id="pool-error" className="text-[11px] text-danger block">
              {fieldErrors.pool}
            </span>
          )}
          <span className="text-[11px] text-fg-subtle block">
            Photos preferred — drop them anywhere on this form, paste from
            clipboard, or use the Attach button. Or paste an Arena-format
            pool from MTGA. You can mix both: any text plus any images.
          </span>

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(",")}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                void addFiles(e.target.files);
                e.target.value = "";
              }
            }}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={running || images.length >= MAX_IMAGES}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-border bg-bg-raised/50 hover:bg-bg-raised text-xs text-fg-muted hover:text-fg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                images.length >= MAX_IMAGES
                  ? `Max ${MAX_IMAGES} images`
                  : "Attach pool photo (or paste / drop)"
              }
            >
              <Paperclip size={14} />
              {images.length > 0
                ? `${images.length}/${MAX_IMAGES} attached`
                : "Attach photo"}
            </button>
            {isSubscriber && (
              <div
                className="flex items-center gap-2 text-xs"
                title="Subscriber perk: pick the builder model. Sonnet is faster and ~5x cheaper; Opus is slower with deeper analysis."
              >
                <label className="text-fg-muted">Model</label>
                <div className="flex rounded-md border border-border overflow-hidden">
                  {(["sonnet", "opus"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      disabled={running}
                      onClick={() => setModelPreference(m)}
                      className={cn(
                        "px-2.5 py-1 text-xs capitalize transition-colors",
                        modelPreference === m
                          ? "bg-accent/20 text-accent"
                          : "text-fg-muted hover:bg-bg-raised",
                      )}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {running ? (
            <MagicButton
              variant="ghost"
              className="h-10 text-sm"
              onClick={onCancel}
            >
              <Square size={14} strokeWidth={2} />
              Stop
            </MagicButton>
          ) : (
            <LiquidButton
              className="h-10 text-sm"
              disabled={!canSubmit}
              onClick={() => {
                const form = document.querySelector("form");
                form?.requestSubmit();
              }}
              palette={MANA_G}
              trailing={
                <span className="text-[10px] font-mono opacity-70">
                  · {ACTION_COSTS.draft_build}
                </span>
              }
            >
              <Upload size={14} strokeWidth={2} />
              Build my deck
            </LiquidButton>
          )}
        </div>
      </form>

      {(submittedDeck || lastText.length > 0) &&
        (events.length > 0 || running || lastText.length > 0) && (
          <StreamingDeckResult
            buffer={lastText}
            meta={streamMetaQuery.data}
            seedDeck={submittedDeck}
            sectionOrder={SECTION_ORDER}
          />
        )}

      {(events.length > 0 || running) && (
        <StreamingOutput events={events} running={running} lastText={lastText} />
      )}
    </div>
  );
}
